/*
 * @Author: mina.min
 * @Date: 2025-06-20 16:52:08
 * @LastEditors: mina.min
 * @LastEditTime: 2025-08-27 17:11:10
 * @FilePath: /OTA-frontend/src/services/firmware.ts
 * @Description:
 */
import authStore from '@/stores/authStore'
import type { ApiResponse } from '@/types'
import api from './api'

export interface FirmwareItem {
  firmwareId: string
  projectNumber: string
  phaseId: string | null
  phaseName: string | null
  moduleId: string | null
  moduleName: string
  softwareId: number | null
  version: number
  fileName: string
  fileSize: number
  remark: string | null
  status: string
  hidden: boolean
  sort: number
  createBy: string
  updateBy: string
  createTime: string
  updateTime: string
}

export interface FirmwareListResponse {
  code: string
  info: string
  data: {
    total: number
    pageSize: number
    list: FirmwareItem[]
    pageNum: number
  }
}

export const getFirmwareList = async (params: {
  pageNum?: number
  pageSize?: number
  keyword?: string
  phaseName?: string
  moduleName?: string
  status?: string
  hidden?: number
}): Promise<FirmwareListResponse> => {
  const response = await api.get('/OTA-FirmwareHandler/list', { params })
  return response as unknown as FirmwareListResponse
}

export const addFirmware = async (data: {
  firmwareId: string
  moduleId: string
  version: number
  file: File
  remark: string
  hidden: boolean
  status: string
  phaseId?: string
  softwareId?: number
}): Promise<{ code: string; info: string; data: null }> => {
  const formData = new FormData()
  formData.append('firmwareId', data.firmwareId)
  formData.append('moduleId', data.moduleId)
  formData.append('version', String(data.version))
  formData.append('file', data.file)
  formData.append('fileName', data.file.name)
  formData.append('fileSize', String(data.file.size))
  formData.append('remark', data.remark)
  formData.append('hidden', String(data.hidden))
  formData.append('status', data.status)
  formData.append('username', authStore.user?.username || '')
  if (data.phaseId) formData.append('phaseId', data.phaseId)
  if (data.softwareId) formData.append('softwareId', String(data.softwareId))
  const response = await api.post('/OTA-FirmwareHandler/save', formData)
  return response as unknown as { code: string; info: string; data: null }
}

export const getFirmwareDetail = async (firmwareId: string): Promise<FirmwareItem | null> => {
  const response = await api.get('/OTA-FirmwareHandler/detail', {
    params: { firmwareId },
  })
  if (response.code === '200') {
    return response.data
  }
  return null
}

export const editFirmware = async (data: {
  firmwareId: string
  phaseId?: string
  moduleId?: string
  softwareId?: number
  version?: number
  remark?: string
  status?: string
  hidden?: boolean
  file?: File
}): Promise<{ code: string; info: string; data: string }> => {
  const formData = new FormData()
  formData.append('firmwareId', data.firmwareId)
  if (data.phaseId) formData.append('phaseId', data.phaseId)
  if (data.moduleId) formData.append('moduleId', data.moduleId)
  if (data.softwareId) formData.append('softwareId', String(data.softwareId))
  if (data.version) formData.append('version', String(data.version))
  if (data.remark) formData.append('remark', data.remark)
  if (data.status) formData.append('status', data.status)
  if (data.hidden !== undefined) formData.append('hidden', String(data.hidden))
  if (data.file) formData.append('file', data.file)

  const response = await api.post('/OTA-FirmwareHandler/edit', formData)
  return response as unknown as { code: string; info: string; data: string }
}

export const deleteFirmware = async (
  firmwareId: string
): Promise<{ code: string; info: string; data: string }> => {
  const response = await api.post('/OTA-FirmwareHandler/delete', JSON.stringify({ firmwareId }), {
    headers: { 'Content-Type': 'application/json' },
  })
  return response as unknown as { code: string; info: string; data: string }
}

export interface SortItem {
  firmwareId: string
  sort: number
}

export const sortFirmware = async (
  sortList: SortItem[]
): Promise<{ code: string; info: string; data: string }> => {
  const requestData = {
    sortList: sortList.map(item => ({
      firmwareId: String(item.firmwareId),
      sort: item.sort,
    })),
  }

  const response = await api.post('/OTA-FirmwareHandler/sort', JSON.stringify(requestData), {
    headers: { 'Content-Type': 'application/json' },
  })
  return response as unknown as { code: string; info: string; data: string }
}

export interface Firmware {
  id: string
  version: string
  moduleName: string
  fileName: string
  fileSize: number
  s3Url: string
  createdTime: string
  lastUpdated: string
}

export interface UploadFirmwareData {
  file: File
  version: string
  moduleId: string
}

export const uploadFirmware = (data: UploadFirmwareData) => {
  const formData = new FormData()
  formData.append('file', data.file)
  formData.append('version', data.version)
  formData.append('moduleId', data.moduleId)

  return api.post('/firmware/upload', formData)
}

// 下载固件文件
export const downloadFirmware = async (firmwareId: string): Promise<ApiResponse<string>> => {
  return await api.get('/OTA-FirmwareHandler/download', { params: { firmwareId } })
}

// 根据模块ID和阶段ID获取固件列表（用于Campaign选择）
export const getFirmwareListByModules = async (params: {
  phaseIds?: string[]
  moduleIds?: string[]
}): Promise<{
  code: string
  info: string
  data: Array<{ id: string; moduleName: string; version: number }>
}> => {
  const response = await api.get('/OTA-FirmwareHandler/getFirmwareList', {
    params: {
      phaseIds: params.phaseIds,
      moduleIds: params.moduleIds,
    },
  })
  return response as unknown as {
    code: string
    info: string
    data: Array<{ id: string; moduleName: string; version: number }>
  }
}

// 分片上传相关类型定义
export interface MultipartInitRequest {
  fileName: string
  fileSize: number
  contentType: string
  phaseId?: string
  moduleId?: string
  version: number
  chunkSize: number
  expiresInSec: number
}

export interface MultipartInitResponse {
  code: string
  info: string
  data: {
    s3Bucket: string
    cosBucket: string // 添加 cosBucket 字段
    objectKey: string
    s3UploadId: string
    cosUploadId: string
    chunkSize: number
    totalChunks: number
    s3PresignedUrls: Array<{
      partNumber: number
      url: string
      expiresAt: string // 添加 expiresAt 字段
    }>
    cosPresignedUrls: Array<{
      partNumber: number
      url: string
      expiresAt: string // 添加 expiresAt 字段
    }>
  }
}

export interface MultipartPart {
  partNumber: number
  eTag: string
}

export interface MultipartCompleteRequest {
  // S3 信息
  s3Bucket: string
  objectKey: string
  s3UploadId: string
  s3Parts: MultipartPart[]

  // COS 信息
  cosUploadId: string
  cosParts: MultipartPart[]

  // 固件元数据
  phaseId: string
  moduleId: string
  softwareId: number
  version: number
  remark: string
  status: string
  hidden: boolean
  fileName: string
  fileSize: number
  fileHash: string
}

export interface MultipartCompleteResponse {
  code: string
  info: string
  data: null
}

// 分片上传初始化接口
export const initMultipartUpload = async (
  data: MultipartInitRequest
): Promise<MultipartInitResponse> => {
  // 根据HTML示例，确保请求格式正确
  const requestData = {
    fileName: data.fileName,
    fileSize: data.fileSize,
    contentType: data.contentType,
    phaseId: data.phaseId,
    moduleId: data.moduleId,
    version: data.version,
    chunkSize: data.chunkSize,
    expiresInSec: data.expiresInSec,
  }

  const requestBody = JSON.stringify(requestData)

  try {
    const response = await api.post('/OTA-FirmwareHandler/multipart/initWithPresign', requestBody, {
      headers: { 'Content-Type': 'application/json' },
    })

    // 检查响应状态
    if (response && typeof response === 'object' && 'code' in response && response.code === '500') {
      const errorInfo = (response as any).info || '未知错误'
      console.error('[MULTIPART] 服务器内部错误:', errorInfo)
      throw new Error(`服务器内部错误: ${errorInfo}`)
    }

    return response as unknown as MultipartInitResponse
  } catch (error) {
    console.error('[MULTIPART] 初始化请求失败:', error)
    throw error
  }
}

// 分片上传完成接口
export const completeMultipartUpload = async (
  data: MultipartCompleteRequest
): Promise<MultipartCompleteResponse> => {
  const requestBody = JSON.stringify(data)

  const response = await api.post('/OTA-FirmwareHandler/multipart/complete', requestBody, {
    headers: { 'Content-Type': 'text/plain' },
  })

  return response as unknown as MultipartCompleteResponse
}

export interface MultipartCompleteEditRequest extends MultipartCompleteRequest {
  firmwareId: string
}

export const completeMultipartUploadEdit = async (
  data: MultipartCompleteEditRequest
): Promise<MultipartCompleteResponse> => {
  const requestBody = JSON.stringify(data)

  const response = await api.post('/OTA-FirmwareHandler/multipart/completeEdit', requestBody, {
    headers: { 'Content-Type': 'text/plain' },
  })

  return response as unknown as MultipartCompleteResponse
}

// 简单的字符串哈希函数（备用方案）
const simpleHash = (str: string): string => {
  let hash = 0
  if (str.length === 0) return hash.toString()
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16)
}

// 计算文件哈希值
export const calculateFileHash = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    // 超大文件直接返回备用哈希，避免一次性读入内存导致浏览器崩溃
    const SAFE_HASH_LIMIT = 512 * 1024 * 1024 // 512MB，上限用于内存安全
    if (file.size > SAFE_HASH_LIMIT) {
      console.warn('[HASH] 文件过大，跳过内存哈希，返回备用哈希')
      const fallbackHash = `fallback-${file.name}-${file.size}-${file.lastModified}`
      resolve(fallbackHash)
      return
    }
    // 检查 crypto.subtle 是否可用
    if (!crypto || !crypto.subtle) {
      console.warn('[HASH] Web Crypto API 不可用，使用备用哈希方法')
      // 使用备用方法：基于文件名和大小的简单哈希
      const fallbackHash = simpleHash(`${file.name}-${file.size}-${file.lastModified}`)
      resolve(fallbackHash)
      return
    }

    const reader = new FileReader()
    reader.onload = async e => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer
        if (!arrayBuffer) {
          reject(new Error('无法读取文件数据'))
          return
        }

        // 检查 crypto.subtle.digest 是否可用
        if (typeof crypto.subtle.digest !== 'function') {
          console.warn('[HASH] crypto.subtle.digest 不可用，使用备用哈希方法')
          const fallbackHash = simpleHash(`${file.name}-${file.size}-${file.lastModified}`)
          resolve(fallbackHash)
          return
        }

        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
        resolve(hashHex)
      } catch (error) {
        console.error('[HASH] 计算哈希失败:', error)
        console.warn('[HASH] 使用备用哈希方法')
        try {
          const fallbackHash = simpleHash(`${file.name}-${file.size}-${file.lastModified}`)
          resolve(fallbackHash)
        } catch (fallbackError) {
          reject(new Error(`哈希计算失败: ${(error as Error).message}`))
        }
      }
    }
    reader.onerror = error => {
      console.error('[HASH] 文件读取失败:', error)
      reject(new Error('文件读取失败'))
    }
    reader.readAsArrayBuffer(file)
  })
}

// 带重试的分片上传
const uploadChunkWithRetry = async (
  chunk: Blob,
  s3Url: string,
  cosUrl: string,
  partNumber: number,
  maxRetries: number = 2 // 减少重试次数从3次到2次
): Promise<{ partNumber: number; s3ETag: string; cosETag: string }> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // 错峰上传 S3 和 COS，避免同时并发
      const s3Promise = uploadChunk(chunk, s3Url, 'S3', partNumber)
      await new Promise(resolve => setTimeout(resolve, 50)) // 50ms 错峰
      const cosPromise = uploadChunk(chunk, cosUrl, 'COS', partNumber)

      const [s3ETag, cosETag] = await Promise.all([s3Promise, cosPromise])

      return { partNumber, s3ETag, cosETag }
    } catch (error) {
      console.warn(`[UPLOAD] Part #${partNumber} 第 ${attempt} 次尝试失败:`, error)

      if (attempt === maxRetries) {
        throw new Error(`Part #${partNumber} 上传失败: ${(error as Error).message}`)
      }

      // 指数退避重试
      const delay = Math.min(500 * Math.pow(2, attempt - 1), 2000) // 减少延迟时间
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }

  throw new Error(`Part #${partNumber} 上传失败: 超过最大重试次数`)
}

// 上传单个分片
const uploadChunk = async (
  chunk: Blob,
  url: string,
  target: string,
  partNumber: number
): Promise<string> => {
  const start = performance.now()

  const response = await fetch(url, {
    method: 'PUT',
    body: chunk,
    headers: {
      'Content-Type': 'application/octet-stream',
    },
  })

  const end = performance.now()

  if (!response.ok) {
    console.error(
      `[${target}] 分片 #${partNumber} 失败: ${response.status} ${response.statusText} (${(end - start).toFixed(0)}ms)`
    )
    throw new Error(`${target} 上传失败: ${response.status} ${response.statusText}`)
  }

  const eTag = response.headers.get('ETag')
  if (!eTag) {
    throw new Error(`${target} 未返回 ETag`)
  }

  return eTag.replace(/"/g, '') // 移除 ETag 的引号
}

// 分片上传文件（优化版本）
export const uploadFileChunks = async (
  file: File,
  uploadContext: MultipartInitResponse['data'],
  onProgress?: (progress: number) => void,
  maxConcurrent: number = 2
): Promise<{ s3Parts: MultipartPart[]; cosParts: MultipartPart[] }> => {
  const s3Parts: MultipartPart[] = []
  const cosParts: MultipartPart[] = []

  // 分批并发上传
  for (let i = 0; i < uploadContext.totalChunks; i += maxConcurrent) {
    const batch = []
    const batchEnd = Math.min(i + maxConcurrent, uploadContext.totalChunks)

    for (let j = i; j < batchEnd; j++) {
      const partNumber = j + 1
      const start = j * uploadContext.chunkSize
      const end = Math.min(start + uploadContext.chunkSize, file.size)
      const chunk = file.slice(start, end)

      const s3Url = uploadContext.s3PresignedUrls.find(u => u.partNumber === partNumber)?.url
      const cosUrl = uploadContext.cosPresignedUrls.find(u => u.partNumber === partNumber)?.url

      if (!s3Url || !cosUrl) {
        throw new Error(`缺少分片 ${partNumber} 的预签名URL`)
      }

      batch.push(uploadChunkWithRetry(chunk, s3Url, cosUrl, partNumber))
    }

    const batchResults = await Promise.all(batch)

    for (const result of batchResults) {
      s3Parts.push({ partNumber: result.partNumber, eTag: result.s3ETag })
      cosParts.push({ partNumber: result.partNumber, eTag: result.cosETag })
    }

    // 更新进度
    const progress = Math.round(((i + batch.length) / uploadContext.totalChunks) * 80) // 分片上传占80%的进度
    onProgress?.(progress)

    // 批次间稍作延迟，避免过于频繁的请求
    if (batchEnd < uploadContext.totalChunks) {
      await new Promise(resolve => setTimeout(resolve, 100))
    }
  }

  return { s3Parts, cosParts }
}